import * as THREE from "three";
import { CommandResult, Commands } from "@highmarch/domain";
import type { Command, GearItem, LevelSim } from "@highmarch/domain";
import type { WyrmGrant } from "@highmarch/app";
import { wyrmRetryGems } from "@highmarch/app";
import { audio } from "../audio/audio";
import type { MusicTrack } from "../audio/audio";
import { RareWorld } from "../render/rareWorld";
import type { SceneRig } from "../render/scene";
import { groundAt } from "../render/terrain";
import { WYRM_NIGHT } from "../render/wyrmLook";
import { WyrmSky } from "../render/wyrmSky";
import { WyrmSignal, WyrmDef, readWyrmSignals, wyrmChoiceUp, wyrmDef, wyrmHpLeft, wyrmNow } from "../rare/wyrmApi";
import type { HudView } from "../ui/hud";
import { reducedVfx } from "../ui/settings";
import { WyrmChoiceSheet } from "../ui/wyrmChoice";
import { WYRM_COPY, WYRM_ESCAPE, WYRM_REVEAL, arrivalPlan, revealPlan, roarCrossed, wyrmChoiceModel } from "../ui/wyrmModel";
import { WyrmRevealCard } from "../ui/wyrmReveal";
import type { WyrmStore } from "../ui/wyrmTopUp";
import { WyrmBanner, WyrmEscape, WyrmFarewell, legendBar, paintWyrm, unlegendBar } from "../ui/wyrmUi";
import type { HitStop } from "./hitStop";
import type { LevelScene } from "./levelScene";
import { wyrmFleeFx, wyrmKillBurst, wyrmRoarFx, wyrmSlam } from "./wyrmFx";
import { WyrmAudio } from "./wyrmAudio";

const _ray = new THREE.Raycaster(), _ndc = new THREE.Vector2(), _ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), _portal = new THREE.Vector3();
/** The ground (y = 0) under a point of the screen (normalised device coordinates), or null. */
function groundUnder(camera: THREE.Camera, x: number, y: number): THREE.Vector3 | null {
  _ray.setFromCamera(_ndc.set(x, y), camera);
  return _ray.ray.intersectPlane(_ground, new THREE.Vector3());
}

/** What the moment needs from outside the level (boot levelFlow → MetaFlow, the skins link); each may be absent. */
export interface WyrmHooks {
  /** The kill's grant (app grantWyrmKill: granted once and saved), for the reveal. */
  grant(): WyrmGrant | null;
  /** The complete-set chest's gear (empty for a skin). */
  gear(): readonly GearItem[];
  /** Pays for another try (app buyWyrmRetry, saved): true, or why not. */
  buyRetry(): true | string;
  gems(): number;
  /** Wears an unlocked Starfall skin now (Cosmetics.equip): true, or why not. */
  equip(skinId: string): true | string;
  /** The gem store for the choice's top-up (ui/wyrmTopUp.ts), or null when this build has none. */
  store(): WyrmStore | null;
}

/**
 * The Starfall Wyrm as the 1-in-1,000 spectacle (M40). Reads its signals each tick (rare/wyrmApi.ts) and stages them;
 * presentation only — the one thing it sends the sim is the Commander's answer to the choice, as a logged command.
 *
 * Arrival (ui/wyrmModel.ts arrivalPlan): the night falls at once (the rig's RareGlow with WYRM_NIGHT) with the starfield
 * and a meteor shower (render/wyrmSky.ts), the shimmer swell plays and the camera leans in as it dives; on the dive's
 * downbeat the slam (flash, shockwave, star burst, exposure punch, trauma) and the banner's title; the roar's braam;
 * Bram; the legend bar; the rare bed. Alive: shine and comet trail (EnemyViews), roars at 75/50/25 %. Kill: slow motion,
 * the prismatic burst, WYRM SLAIN!, the reveal (skin or hoard). Escape: the flee soars off, THE WYRM ESCAPES!, then the
 * choice sheet; a retry returns with a shorter dive ("IT RETURNS!"), a release lifts the night with Bram's farewell.
 */
export class WyrmMoment {
  private readonly banner: WyrmBanner;
  private readonly reveal: WyrmRevealCard;
  private readonly escape: WyrmEscape;
  private readonly farewell: WyrmFarewell;
  private readonly choice: WyrmChoiceSheet;
  private readonly frameEl: HTMLDivElement;
  private readonly def: WyrmDef;
  private world: RareWorld | null = null;
  private sky: WyrmSky | null = null;
  private readonly signals: WyrmSignal[] = [];
  /** The night is up for it: alive, or waiting on the choice. */
  private up = false;
  private hpFrac = 1;
  private camBack: { x: number; z: number; zoom: number } | null = null;
  private timers: number[] = [];
  private slowLeft = 0;
  private bedBefore: MusicTrack | null = null;
  private disposed = false;
  private resumeChecked = false;
  hooks: WyrmHooks | null = null;
  /** M40: its own sound set (level/wyrmAudio.ts). */
  private readonly sound = new WyrmAudio();

  constructor(private readonly sim: LevelSim, private readonly rig: SceneRig, private readonly scene: LevelScene, private readonly hud: HudView, private readonly hitStop: HitStop, uiRoot: HTMLElement, private readonly execute: (c: Command) => CommandResult) {
    this.def = wyrmDef(sim.balance);
    this.banner = new WyrmBanner(uiRoot);
    this.escape = new WyrmEscape(uiRoot);
    this.farewell = new WyrmFarewell(uiRoot);
    this.choice = new WyrmChoiceSheet(uiRoot);
    this.reveal = new WyrmRevealCard(uiRoot, () => hud.root.getBoundingClientRect());
    this.frameEl = document.createElement("div");
    this.frameEl.className = "wyrm-frame";
    paintWyrm(this.frameEl, this.def);
    uiRoot.prepend(this.frameEl);
  }

  /** The Wyrm's night is up (alive, or the choice open). */
  get active(): boolean { return this.up; }
  /** The choice sheet is on screen. */
  get choosing(): boolean { return this.choice.shown; }

  /** After a sim tick or a command: the signals from `from` on, then its roars as it is hurt. */
  observe(from: number): void {
    if (this.disposed) return;
    this.signals.length = 0;
    readWyrmSignals(this.sim, from, this.signals);
    for (const s of this.signals) {
      if (s.kind === "spawned") this.spawned(s.attempt > 1, false);
      else if (s.kind === "killed") this.killed();
      else if (s.kind === "fled") this.fled();
      else if (s.kind === "retried") this.choice.hide();
      else this.released();
    }
    const w = this.sim.wyrm, e = w ? this.sim.findEnemy(w.enemyId) : null;
    if (!e) return;
    const frac = e.hp.toNumber() / Math.max(1e-6, e.maxHp.toNumber());
    if (roarCrossed(this.hpFrac, frac) >= 0 && this.scene.enemies.wyrmRoar(e.id)) { this.sound.roar(); wyrmRoarFx(this.scene, this.rig, this.def, reducedVfx()); }
    this.hpFrac = frac;
    this.sound.health(frac);
  }

  private later(ms: number, fn: () => void): void { this.timers.push(window.setTimeout(() => { if (!this.disposed) fn(); }, ms)); }

  /** The night, the sky, the frame and the bar for it (a fresh arrival, a retry or a resumed battle). */
  private nightFalls(): void {
    if (!this.up) this.bedBefore = audio.track;
    this.up = true;
    this.rig.rareGlow.spawned(WYRM_NIGHT);
    this.frameEl.classList.add("on");
    legendBar(this.hud.bossBar, this.def);
    const g = this.sim.grid;
    if (!this.world) { this.world = new RareWorld(g.width, g.height, groundAt); this.rig.scene.add(this.world.points); this.world.setColours(this.def.sparkA, this.def.sparkC); }
    if (!this.sky) { this.sky = new WyrmSky(g.width, g.height, [this.def.sparkA, this.def.sparkB, this.def.sparkC]); this.rig.scene.add(this.sky.points); }
  }

  /** `quiet`: a resumed battle with it already on the field — the look, the bed and the bar, no entrance. */
  private spawned(retry: boolean, quiet: boolean): void {
    this.nightFalls();
    const w = this.sim.wyrm, e = w ? this.sim.findEnemy(w.enemyId) : null;
    this.hpFrac = e ? e.hp.toNumber() / Math.max(1e-6, e.maxHp.toNumber()) : 1;
    const toRare = () => { if (audio.track === "battle" || audio.track === "boss") audio.music("rare"); };
    this.sound.arrive(retry, quiet);
    if (quiet) { toRare(); return; }
    const reduced = reducedVfx();
    this.sky?.shower(retry ? 0.8 : 1.4);
    const pct = Math.max(1, Math.round(100 * Math.min(1, this.hpFrac)));
    const sub = !retry ? WYRM_COPY.arrivalSub : WYRM_COPY.returnSub(pct);
    const plan = this.leanPlan();
    this.banner.show(this.def, retry, sub, plan.portalY < WyrmMoment.BannerLowBelow);
    for (const b of arrivalPlan(retry, reduced)) {
      if (b.beat === "shake") this.later(b.at, () => this.lean(plan.target));
      else if (b.beat === "slam") this.later(b.at, () => { wyrmSlam(this.scene, this.rig, this.def, reducedVfx(), !retry); this.hitStop.request(90, { force: true }); toRare(); });
      else if (b.beat === "roar") this.later(b.at, () => { if (!reducedVfx()) this.rig.iso.addTrauma(0.3); });   // the roar is wyrmAudio.arrive's
      else if (b.beat === "camBack") this.later(b.at, () => { if (this.camBack && !reducedVfx()) this.rig.iso.followTo(this.camBack, 0.9); this.camBack = null; });
    }
  }

  /**
   * Where the camera leans as it dives: in toward the portal, framing it high on the screen (`LeanScreenY`), and where
   * the portal will then land on screen (0 top … 1 bottom) — the look point is clamped to the map like a drag, so a
   * portal on the map's edge may stay elsewhere; the banner takes the other half of the screen (`WyrmBanner.show`).
   */
  private leanPlan(): { target: { x: number; z: number; zoom: number }; portalY: number } {
    const iso = this.rig.iso, cam = this.rig.camera, portal = this.sim.road.portal, view = iso.viewState();
    const zoom = Math.max(0.24, view.zoom * WyrmMoment.LeanZoom);
    // the ground under the screen's centre and under the spot the portal should land on, with the camera as it is: the
    // look point goes that far beyond the portal (scaled by the zoom it eases to)
    const c = groundUnder(cam, 0, 0), at = groundUnder(cam, 0, 1 - 2 * WyrmMoment.LeanScreenY);
    const k = zoom / Math.max(1e-3, view.zoom), dx = c && at ? (c.x - at.x) * k : 0, dz = c && at ? (c.z - at.z) * k : 0;
    const want = reducedVfx() ? { ...view } : { x: portal.x + dx, z: portal.y + dz, zoom };
    // try the view there (clamped as the camera clamps it), read where the portal lands, and put the view back
    iso.restoreView({ ...want, yaw: view.yaw });
    const clamped = iso.viewState();
    cam.updateMatrixWorld();
    const p = _portal.set(portal.x, groundAt(portal.x, portal.y), portal.y).project(cam);
    iso.restoreView(view);
    cam.updateMatrixWorld();
    return { target: { x: clamped.x, z: clamped.z, zoom: clamped.zoom }, portalY: (1 - p.y) / 2 };
  }

  /** The lean itself (at the dive's start): the camera eases there and the ground starts to tremble. */
  private lean(target: { x: number; z: number; zoom: number }): void {
    if (reducedVfx()) return;
    const view = this.rig.iso.viewState();
    this.camBack = { x: view.x, z: view.z, zoom: view.zoom };
    this.rig.iso.followTo(target, 0.6);
    this.rig.iso.addTrauma(0.22);
  }
  /** Where the portal lands on screen as it dives (fraction of the height from the top), and how far the camera leans in. */
  static readonly LeanScreenY = 0.32;
  static readonly LeanZoom = 0.82;
  /** The banner sits in the lower half when the portal lands above this (fraction of the screen), else in the upper. */
  static readonly BannerLowBelow = 0.56;

  private killed(): void {
    const reduced = reducedVfx();
    this.banner.hide();
    const drawn = this.scene.enemies.rareDrawn();
    if (!reduced) { this.hitStop.request(240, { ramp: true, force: true }); this.slowLeft = WyrmMoment.SlowSeconds; }
    wyrmKillBurst(this.scene, this.rig, this.def, drawn ? drawn.centre.clone() : null, drawn ? drawn.feet.clone() : null, drawn?.height ?? 0.9, reduced);
    this.hud.showCallout(WYRM_COPY.slainTitle, WYRM_COPY.slainSub, "bossDown wyrm");
    this.up = false;
    this.later(WyrmMoment.NightHoldMs, () => this.lift(false));   // the burst plays against the night, then the sun breaks
    this.later(WYRM_REVEAL.DelayMs, () => {
      const plan = revealPlan(this.sim.balance, this.hooks?.grant() ?? null);
      if (plan) this.sound.unlock();
      if (plan) this.reveal.show(this.def, plan, this.sim.balance, this.hooks?.gear() ?? [], this.hooks ? (id) => this.hooks!.equip(id) : null);
    });
  }

  private fled(): void {
    this.banner.hide();
    this.sound.flee();
    wyrmFleeFx(this.scene, this.rig, this.def, reducedVfx());
    this.later(WYRM_ESCAPE.RibbonMs, () => this.escape.show(this.def));
    this.later(WYRM_ESCAPE.ChoiceMs, () => this.openChoice());
  }

  private released(): void {
    this.choice.hide();
    this.escape.hide();
    this.farewell.show(this.def);
    this.lift(true);
  }

  /** The night lifts (the sun-break after a kill; a gentler fade when it is let go), the bed and the bar come back. */
  private lift(gentle: boolean): void {
    if (gentle) this.rig.rareGlow.escaped(); else this.rig.rareGlow.killed();
    this.frameEl.classList.remove("on");
    this.up = false;
    this.later(1600, () => this.bedBack());
    this.later(WyrmMoment.BarBackMs, () => { if (!this.up) unlegendBar(this.hud.bossBar); });
  }

  private bedBack(): void {
    if (audio.track === "rare") audio.music(this.bedBefore === "rare" || this.bedBefore === null ? "battle" : this.bedBefore);
  }

  /** The sheet, when the choice is (still) open and the battle not over. */
  private openChoice(): void {
    if (!wyrmChoiceUp(this.sim) || this.choice.shown) return;
    this.escape.hide();
    this.choice.show(this.def, this.choiceModel(), { retry: () => this.choose("retry"), release: () => { this.choose("release"); }, store: this.hooks?.store() ?? null }, () => this.choiceModel());
    this.sound.choice();
  }

  private choiceModel() {
    const hp = wyrmHpLeft(this.sim) ?? { left: 1, max: 1 };
    return wyrmChoiceModel({ gems: this.hooks?.gems() ?? 0, cost: wyrmRetryGems(this.sim.balance), hpLeft: hp.left, hpMax: hp.max, name: this.def.name, store: !!this.hooks?.store() });
  }

  /** The Commander's answer (the sheet's buttons, and the captures' debug hook): true, or why not. */
  choose(kind: "retry" | "release"): true | string {
    if (!wyrmChoiceUp(this.sim)) return "no choice";
    if (kind === "release") return this.execute(Commands.wyrmRelease()) === CommandResult.Ok ? true : "no choice";
    const paid = this.hooks ? this.hooks.buyRetry() : "no choice";
    if (paid !== true) return paid;
    return this.execute(Commands.wyrmRetry()) === CommandResult.Ok ? true : "no choice";
  }

  /** Real seconds of the kill's slow motion and how slow it starts (easing back to 1×). */
  static readonly SlowSeconds = 1.8;
  static readonly SlowFrom = 0.2;
  /** After the kill, how long the night holds (the slow motion and the burst) before the sun breaks through, ms. */
  static readonly NightHoldMs = 1300;
  /** After it is gone, the boss bar (hidden by then) turns back into a plain one. */
  static readonly BarBackMs = 1800;

  /** The kill's slow-motion multiplier this frame (1 when none). */
  slowScale(dt: number): number {
    if (this.slowLeft <= 0) return 1;
    this.slowLeft = Math.max(0, this.slowLeft - dt);
    const k = 1 - this.slowLeft / WyrmMoment.SlowSeconds;
    return WyrmMoment.SlowFrom + (1 - WyrmMoment.SlowFrom) * k * k;
  }

  /** Every frame: the sky and the world sparkles follow the night's weight; a resumed battle picks up where it was. */
  frame(dt: number): void {
    if (this.disposed) return;
    if (!this.resumeChecked) {
      this.resumeChecked = true;
      if (!this.sim.isOver && wyrmNow(this.sim) && !this.up) this.spawned(false, true);
      else if (wyrmChoiceUp(this.sim)) { this.nightFalls(); this.openChoice(); }
    }
    const w = this.rig.rareGlow.weights, reduced = reducedVfx();
    this.world?.update(dt, w.sparkle, reduced);
    this.sky?.update(dt, w.sparkle, reduced);
    const now = wyrmNow(this.sim);
    this.sound.frame(now && !this.choice.shown ? this.scene.enemies.wyrmWing(now.enemyId) : null, this.sim.tick);
    // gone from the sim with no signal (a rebuilt level): let the night and the bed go
    if (this.up && !wyrmNow(this.sim) && !wyrmChoiceUp(this.sim) && this.scene.enemies.rareDrawn() === null) this.lift(true);
  }

  /** The level ended: the banner and the choice go; a reveal mid-flight finishes. */
  stop(): void { this.banner.hide(); this.choice.hide(); this.slowLeft = 0; }

  dispose(): void {
    this.disposed = true;
    for (const t of this.timers) window.clearTimeout(t);
    this.timers = [];
    this.banner.hide(); this.reveal.hide(); this.escape.hide(); this.farewell.hide(); this.choice.hide();
    this.frameEl.remove();
    this.world?.dispose(); this.world = null;
    this.sky?.dispose(); this.sky = null;
    if (this.up) this.rig.rareGlow.reset();
    unlegendBar(this.hud.bossBar);
  }
}
